import React, { useEffect, useState } from "react";
import NiceModal, { NiceModalHocProps, useModal } from "@ebay/nice-modal-react";
import { tt } from "../../../utils/i18n/i18n";
import KC_Modal_2 from "../../../designsystem/KC_Modal/KC_Modal_2";
import KC_Heading3 from "../../../designsystem/typography/KC-Heading/KC_Heading3";
import BillJavaScriptDTO from "@tootsweet/model/datatransfer/BillDTO";
import { AxiosForLegacyBackend } from "../../../utils/helpers/AxiosForLegacyBackend";
import { useDispatch } from "react-redux";
import { setError, setLoading } from "../../../redux/actions/app";
import {
  updateCustomerThunk,
  useAccountToDisplaySelector
} from "../../../redux/actions/user";
import PaymentCard from "../../../model/PaymentCard";
import CurrencySubUnitsMap from "../../../model/CurrencySubUnitsMap";
import CONFIG from "../../../model/TSConfig";
import ConfirmModal from "../../../components/modal/ConfirmModal/ConfirmModal";
import "./PayBillModal.scss";
import Address from "@tootsweet/model/entities/Address";
import BusinessUser from "@tootsweet/model/entities/BusinessUser";
import BillType from "@tootsweet/model/entities/enum/BillType";
import { LoadingWrapper } from "../../../components/Loaders/LoadingWrapper";
import ShippingAddressStep from "./pay_flow/ShippingAddressStep";
import ChooseCardStep from "./pay_flow/ChooseCardStep";
import AddCardStep from "./pay_flow/AddCardStep";

interface Props {
  bill: BillJavaScriptDTO;
  businessUserId: string;
  currencySubUnits: CurrencySubUnitsMap;
  onSuccess: () => void;
}

const PayBillModal: React.FC<Props & NiceModalHocProps> =
  NiceModal.create<Props>((props) => {
    const modal = useModal();
    const dispatch = useDispatch();
    const { accountToDisplay } = useAccountToDisplaySelector();
    const [paymentCards, setPaymentCards] = useState<PaymentCard[] | null>(
      null
    );
    const [showAddCard, setShowAddCard] = useState(false);
    const [setupIntentId, setSetupIntentId] = useState<string>("");
    const [clientSecret, setClientSecret] = useState<string>("");
    const [isLoadingCards, setIsLoadingCards] = useState(true);
    const [showShippingForm, setShowShippingForm] = useState(false);
    const [shippingAddress, setShippingAddress] = useState<Address | undefined>(
      accountToDisplay?.businessUser?.shippingAddress
    );

    // Check if shipping address needs to be filled (FR users)
    useEffect(() => {
      if (accountToDisplay?.businessUser?.shippingAddress) {
        setShippingAddress(accountToDisplay.businessUser.shippingAddress);

        if (
          props.bill.billType === BillType.Manual &&
          !!props.bill.posaDeliveryId
        ) {
          setShowShippingForm(true);
        }
      }
    }, [accountToDisplay]);

    const fetchCards = async () => {
      try {
        setIsLoadingCards(true);
        const res = await AxiosForLegacyBackend({
          method: "GET",
          url: `${CONFIG.backendURL}/payment/cards2`,
          params: { businessUserId: props.businessUserId }
        });
        setPaymentCards(res.data);

        if (res.data.length === 0) {
          const setupIntentRes = await AxiosForLegacyBackend({
            method: "POST",
            url: `${CONFIG.backendURL}/payment/setup-intent`,
            params: { businessUserId: props.businessUserId }
          });
          setSetupIntentId(setupIntentRes.data.setupIntentId);
          setClientSecret(setupIntentRes.data.clientSecret);
          setShowAddCard(true);
        }
      } catch (error: any) {
        dispatch(setError(error?.message || tt("api_error")));
      } finally {
        setIsLoadingCards(false);
      }
    };

    useEffect(() => {
      void fetchCards();
    }, [props.businessUserId]);

    const handlePaymentSuccess = async (
      paymentIntentId: string | undefined | null
    ) => {
      if (!paymentIntentId) {
        dispatch(setError(tt("api_error")));
        return;
      }

      dispatch(setLoading(false));

      modal.resolve(true);
      void modal.hide();

      const isPosaOrder =
        props.bill.billType === BillType.Manual && !!props.bill.posaDeliveryId;

      await NiceModal.show(ConfirmModal, {
        title: tt("pay_bill_success_title"),
        children: isPosaOrder
          ? tt("pay_bill_posa_success_message")
          : tt("pay_bill_success_message"),
        hideAction: true
      });

      props.onSuccess();
    };

    const handleAddCardSuccess = async () => {
      setShowAddCard(false);
      await fetchCards();
    };

    const handlePaymentError = async (error: Error | string) => {
      dispatch(setLoading(false));

      // Log technical error details for debugging
      console.error("Payment error details:", error);

      // Always show user-friendly generic message
      await NiceModal.show(ConfirmModal, {
        title: tt("payment_error_title"),
        children: tt("payment_error_generic"),
        hideAction: true
      });
    };

    const goToInsertCard = async () => {
      try {
        dispatch(setLoading(true));
        const setupIntentRes = await AxiosForLegacyBackend({
          method: "POST",
          url: `${CONFIG.backendURL}/payment/setup-intent`,
          params: { businessUserId: props.businessUserId }
        });
        setSetupIntentId(setupIntentRes.data.setupIntentId);
        setClientSecret(setupIntentRes.data.clientSecret);
        setShowAddCard(true);
        dispatch(setLoading(false));
      } catch (error: any) {
        dispatch(setError(error?.message || tt("api_error")));
        dispatch(setLoading(false));
      }
    };

    const handleShippingAddressSubmit = async (values: BusinessUser) => {
      try {
        dispatch(setLoading(true));

        const address = values.shippingAddress;

        // Validate country is France for POSA deliveries
        if (address?.countryCode?.toLowerCase() !== "fr") {
          dispatch(setError(tt("cannot_order_posa_outside_of_france_title")));
          dispatch(setLoading(false));
          return;
        }

        // Validate required fields for FR
        if (!address.line1 || !address.postalCode || !address.city) {
          dispatch(setError(tt("shipping_address_required")));
          dispatch(setLoading(false));
          return;
        }

        // Update business user with new shipping address
        await dispatch(
          updateCustomerThunk(
            values,
            accountToDisplay?.trackingID || "",
            accountToDisplay?.token
          )
        );

        setShippingAddress(address);
        setShowShippingForm(false);
        dispatch(setLoading(false));
      } catch (error: any) {
        dispatch(setError(error?.message || tt("api_error")));
        dispatch(setLoading(false));
      }
    };

    return (
      <KC_Modal_2
        id="pay-bill-modal"
        isOpen={modal.visible}
        onClose={() => {
          modal.resolve(false);
          void modal.hide();
        }}
        header={
          <div className="w-100 py-2 px-3">
            <KC_Heading3
              className="text-center"
              text={
                showShippingForm
                  ? tt("deliveryInfoPlaceholder")
                  : tt("pay_bill_modal_title")
              }
            />
          </div>
        }
      >
        <LoadingWrapper loading={isLoadingCards}>
          <div className="p-4">
            {showShippingForm && accountToDisplay?.businessUser && (
              <ShippingAddressStep
                businessUser={accountToDisplay.businessUser}
                shippingAddress={shippingAddress}
                onSubmit={handleShippingAddressSubmit}
              />
            )}

            {!isLoadingCards &&
              !showShippingForm &&
              !showAddCard &&
              paymentCards &&
              paymentCards.length > 0 && (
                <ChooseCardStep
                  paymentCards={paymentCards}
                  businessUserId={props.businessUserId}
                  bill={props.bill}
                  currencySubUnits={props.currencySubUnits}
                  onPaymentSuccess={handlePaymentSuccess}
                  onPaymentError={handlePaymentError}
                  onAddCard={goToInsertCard}
                />
              )}

            {!isLoadingCards &&
              !showShippingForm &&
              showAddCard &&
              clientSecret && (
                <AddCardStep
                  businessUserId={props.businessUserId}
                  setupIntentId={setupIntentId}
                  clientSecret={clientSecret}
                  onSuccess={handleAddCardSuccess}
                />
              )}
          </div>
        </LoadingWrapper>
      </KC_Modal_2>
    );
  });

export default PayBillModal;
